class MegaNav extends HTMLElement {
  constructor() {
    super();
    this.activePanel = null;
    this.activeTrigger = null;
    this.intentTimer = null;
    this.leaveTimer = null;
  }

  connectedCallback() {
    this.triggers = this.querySelectorAll('[data-mega-trigger]');
    this.backdrop = this.querySelector('[data-mega-backdrop]');
    this.headerWrapper = document.querySelector('.header-wrapper');

    this.triggers.forEach((trigger) => {
      const panel = this.getPanel(trigger);
      if (!panel) return;

      trigger.addEventListener('mouseenter', () => this.onTriggerEnter(trigger, panel));
      trigger.addEventListener('mouseleave', () => this.onTriggerLeave());
      panel.addEventListener('mouseenter', () => this.onPanelEnter());
      panel.addEventListener('mouseleave', () => this.onPanelLeave());

      trigger.addEventListener('focus', () => this.open(trigger, panel));
      trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e, trigger, panel));
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeAll());
    }

    this.onDocKeydown = (e) => {
      if (e.key === 'Escape' && this.activePanel) {
        const trigger = this.activeTrigger;
        this.closeAll();
        if (trigger) trigger.focus();
      }
    };
    document.addEventListener('keydown', this.onDocKeydown);

    this.onFocusOut = () => {
      requestAnimationFrame(() => {
        if (this.activePanel && !this.contains(document.activeElement)) {
          this.closeAll();
        }
      });
    };
    this.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocKeydown);
    this.removeEventListener('focusout', this.onFocusOut);
    clearTimeout(this.intentTimer);
    clearTimeout(this.leaveTimer);
  }

  getPanel(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.querySelector('#' + id) : null;
  }

  onTriggerEnter(trigger, panel) {
    clearTimeout(this.leaveTimer);

    if (this.activePanel && this.activePanel !== panel) {
      this.closeAll();
    }

    this.intentTimer = setTimeout(() => this.open(trigger, panel), 150);
  }

  onTriggerLeave() {
    clearTimeout(this.intentTimer);
    this.scheduleClose();
  }

  onPanelEnter() {
    clearTimeout(this.leaveTimer);
  }

  onPanelLeave() {
    this.scheduleClose();
  }

  scheduleClose() {
    this.leaveTimer = setTimeout(() => this.closeAll(), 100);
  }

  open(trigger, panel) {
    if (this.activePanel === panel) return;
    this.closeAll();

    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('mega-nav__panel--active');
    this.activePanel = panel;
    this.activeTrigger = trigger;

    if (this.backdrop) this.backdrop.classList.add('mega-nav__backdrop--active');
    if (this.headerWrapper) this.headerWrapper.preventHide = true;

    if (!document.documentElement.style.getPropertyValue('--header-bottom-position-desktop')) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        Math.floor(this.headerWrapper.getBoundingClientRect().bottom) + 'px'
      );
    }
  }

  closeAll() {
    clearTimeout(this.intentTimer);
    clearTimeout(this.leaveTimer);

    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-mega-panel]').forEach((p) =>
      p.classList.remove('mega-nav__panel--active')
    );

    if (this.backdrop) this.backdrop.classList.remove('mega-nav__backdrop--active');
    if (this.headerWrapper) this.headerWrapper.preventHide = false;

    this.activePanel = null;
    this.activeTrigger = null;

    removeTrapFocus();
  }

  onTriggerKeydown(e, trigger, panel) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (this.activePanel === panel) {
        this.closeAll();
        trigger.focus();
      } else {
        this.open(trigger, panel);
        const first = panel.querySelector('a, button, [tabindex="0"]');
        trapFocus(panel, first || panel);
      }
    }
  }
}

if (!customElements.get('mega-nav')) {
  customElements.define('mega-nav', MegaNav);
}
